import { HardHat, Users, Briefcase, ShieldCheck } from "lucide-react";

const TIERS = [
  {
    icon: HardHat,
    role: "Site Foreman",
    scope: "On every site · every shift",
    body: "A dedicated foreman is physically present at your premises during every cleaning shift — running the daily check-list, briefing the crew, signing off on the work, and being your single point of contact on the floor.",
    points: [
      "Daily on-site presence & briefing",
      "Crew leadership and time-keeping",
      "Daily quality check + photo evidence",
      "First-line client contact during the shift",
    ],
  },
  {
    icon: Users,
    role: "Service Supervisor",
    scope: "One per service line · weekly on-site",
    body: "Every service line has its own supervisor who owns the contract end-to-end. They train the crew, hold them to KPI / SLA standards, attend the weekly walk-throughs with you, and manage continuous improvement.",
    points: [
      "Weekly on-site quality walk-through",
      "Crew training & KPI / SLA enforcement",
      "Client liaison for scope changes & escalations",
      "Owns the contract performance month-to-month",
    ],
  },
  {
    icon: Briefcase,
    role: "Mobile Manager",
    scope: "Across the region · monthly with you",
    body: "Above the supervisor sits a mobile manager who audits across multiple sites, runs the monthly client review with you, holds the supervisors accountable, and reports directly to the founder.",
    points: [
      "Monthly client review meeting",
      "Cross-site audits & benchmarking",
      "Supervisor coaching & escalations",
      "Direct line into senior management",
    ],
  },
];

export default function SiteLeadership() {
  return (
    <section
      id="site-leadership"
      data-testid="site-leadership-section"
      className="bg-white py-24 md:py-36 border-t border-[#E5EBF2]"
    >
      <div className="container-ags">
        <div className="grid md:grid-cols-12 gap-10 mb-12 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-16 bg-[#2BB6E5]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                Site Leadership Structure
              </span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.035em] text-[#050B14]">
              Always{" "}
              <span className="italic-display font-normal text-[#0E2A57]">
                someone in charge
              </span>{" "}
              on every site.
            </h2>
          </div>
          <p className="md:col-span-5 text-[#475569] text-base md:text-lg leading-[1.6]">
            Three layers of accountability so quality never depends on a single
            person — a foreman on the floor, a supervisor on the contract, and
            a manager auditing across the region.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5EBF2] border border-[#E5EBF2]">
          {TIERS.map((t, i) => {
            const Icon = t.icon;
            return (
              <article
                key={t.role}
                data-testid={`leadership-tier-${i}`}
                className="bg-white p-7 md:p-9 hover:bg-[#F4F8FC] transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="bg-[#0E2A57] text-[#BEE7F5] w-14 h-14 flex items-center justify-center">
                    <Icon size={26} strokeWidth={1.4} />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] mt-2">
                    Tier {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 font-heading font-bold text-xl text-[#0E2A57] tracking-tight">
                  {t.role}
                </h3>
                <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold">
                  {t.scope}
                </div>
                <p className="mt-4 text-[#475569] text-sm md:text-base leading-relaxed">
                  {t.body}
                </p>
                <ul className="mt-5 pt-5 border-t border-[#E5EBF2] space-y-2.5">
                  {t.points.map((p) => (
                    <li key={p} className="text-sm text-[#050B14] flex items-start gap-2.5">
                      <span className="text-[#2BB6E5] font-bold mt-0.5">·</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <div className="mt-10 bg-[#0E2A57] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <span className="bg-white/10 text-[#BEE7F5] w-12 h-12 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={22} />
          </span>
          <div className="flex-1">
            <div className="font-heading font-bold text-lg text-white">
              The chain stops with us.
            </div>
            <p className="mt-2 text-white/75 text-sm md:text-base leading-relaxed">
              Foreman, supervisor, and manager all report into AGS family
              ownership. If your team raises an issue, it goes through three
              accountable people — and the family guarantee backs every one of them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
